/**
 * Accent Color Service
 *
 * Manages per-user accent color preference.
 * Stored in localStorage keyed by user ID so each user gets their own preference.
 * Applied by setting --color-primary and --color-secondary CSS variables on :root,
 * and updating the gradient utility class backgrounds.
 */

export interface AccentColorOption {
 id: string;
 label: string;
 color: string;
 /** Gradient stops for bg-gradient-brand (5 stops, left to right) */
 gradient: string[];
}

/** Predefined accent color options */
export const ACCENT_COLORS: AccentColorOption[] = [
 {
 id: "green",
 label: "Green",
 color: "#00b894",
 gradient: ["#00a88f","#009e93","#089aa3","#0b95b8","#118fd8"],
 },
 {
 id: "blue",
 label: "Blue",
 color: "#3b82f6",
 gradient: ["#2563eb","#3b82f6","#4f8ff7","#6ba3f9","#60a5fa"],
 },
 {
 id: "indigo",
 label: "Indigo",
 color: "#6366f1",
 gradient: ["#4f46e5","#5a54e8","#6366f1","#7577f4","#818cf8"],
 },
 {
 id: "violet",
 label: "Violet",
 color: "#8b5cf6",
 gradient: ["#7c3aed","#8347f0","#8b5cf6","#9d74f8","#a78bfa"],
 },
 {
 id: "pink",
 label: "Pink",
 color: "#ec4899",
 gradient: ["#db2777","#e33d8a","#ec4899","#f06bae","#f472b6"],
 },
 {
 id: "rose",
 label: "Rose",
 color: "#f43f5e",
 gradient: ["#e11d48","#ea2d55","#f43f5e","#f75a76","#fb7185"],
 },
 {
 id: "orange",
 label: "Orange",
 color: "#f97316",
 gradient: ["#ea580c","#f06811","#f97316","#fb8c3c","#fb923c"],
 },
 {
 id: "amber",
 label: "Amber",
 color: "#f59e0b",
 gradient: ["#d97706","#e88c08","#f59e0b","#f7b02e","#fbbf24"],
 },
 {
 id: "teal",
 label: "Teal",
 color: "#14b8a6",
 gradient: ["#0d9488","#109e94","#14b8a6","#2dd4bf","#34d4b5"],
 },
 {
 id: "cyan",
 label: "Cyan",
 color: "#06b6d4",
 gradient: ["#0891b2","#07a3c3","#06b6d4","#22d3ee","#3dd8ef"],
 },
];

const STORAGE_KEY ="tt:accentColor";
const FALLBACK_COLOR_ID ="green";

/** Get the saved accent color ID */
export function getAccentColorId(): string {
 if (typeof window ==="undefined") return FALLBACK_COLOR_ID;
 const saved = localStorage.getItem(STORAGE_KEY);
 if (saved && ACCENT_COLORS.some((c) => c.id === saved)) return saved;
 return FALLBACK_COLOR_ID;
}

/** Get the full accent color option */
export function getAccentColor(): AccentColorOption {
 const id = getAccentColorId();
 return ACCENT_COLORS.find((c) => c.id === id) ?? ACCENT_COLORS[0]!;
}

/** Save accent color preference */
export function setAccentColor(colorId: string): void {
 if (typeof window ==="undefined") return;
 localStorage.setItem(STORAGE_KEY, colorId);
 applyAccentColor(colorId);
}

/**
 * Apply the accent color to the document.
 * Sets CSS variables and updates gradient utility backgrounds.
 */
export function applyAccentColor(colorId?: string): void {
 if (typeof window ==="undefined") return;

 const id = colorId ?? getAccentColorId();
 const option = ACCENT_COLORS.find((c) => c.id === id) ?? ACCENT_COLORS[0]!;

 const root = document.documentElement;

 // Set CSS custom properties
 root.style.setProperty("--color-primary", option.color);
 root.style.setProperty("--color-secondary", option.color);

 // Build gradient string from stops
 const gradientLTR =`linear-gradient(90deg, ${option.gradient[0]} 0%, ${option.gradient[1]} 25%, ${option.gradient[2]} 55%, ${option.gradient[3]} 80%, ${option.gradient[4]} 100%)`;
 root.style.setProperty("--gradient-brand", gradientLTR);

 // Build 10% opacity variant
 const toRgba10 = (hex: string) => {
 const r = parseInt(hex.slice(1, 3), 16);
 const g = parseInt(hex.slice(3, 5), 16);
 const b = parseInt(hex.slice(5, 7), 16);
 return`rgba(${r}, ${g}, ${b}, 0.1)`;
 };
 const toRgba5 = (hex: string) => {
 const r = parseInt(hex.slice(1, 3), 16);
 const g = parseInt(hex.slice(3, 5), 16);
 const b = parseInt(hex.slice(5, 7), 16);
 return`rgba(${r}, ${g}, ${b}, 0.05)`;
 };

 const gradient10 =`linear-gradient(90deg, ${option.gradient.map(toRgba10).join(",")})`;
 const gradient5 =`linear-gradient(90deg, ${option.gradient.map(toRgba5).join(",")})`;
 const gradientTTB =`linear-gradient(0deg, ${option.gradient[0]} 0%, ${option.gradient[2]} 50%, ${option.gradient[4]} 100%)`;

 root.style.setProperty("--gradient-brand-10", gradient10);
 root.style.setProperty("--gradient-brand-5", gradient5);
 root.style.setProperty("--gradient-brand-to-t", gradientTTB);
}

/**
 * Initialize accent color on app boot.
 * Call this early (before first paint if possible).
 */
export function initAccentColor(): void {
 const id = getAccentColorId();
 applyAccentColor(id);
}


